import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, ExternalLink, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WatchMovie() {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [movie, setMovie] = useState(null);
  const [playerUrl, setPlayerUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPlayer, setLoadingPlayer] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMovieAndPlayer();
  }, [movieId]);

  const fetchMovieAndPlayer = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/movies/${movieId}`);
      setMovie(response.data);
      setLoading(false);
      extractPlayer();
    } catch (err) {
      setError('Film non trouvé');
      setLoading(false);
    }
  };

  const extractPlayer = async () => {
    setLoadingPlayer(true);
    setError(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/movies/${movieId}/player`, { headers });
      if (response.data.player_url) {
        setPlayerUrl(response.data.player_url);
      } else {
        setError('Lecteur non trouvé');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoadingPlayer(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-netflix-red animate-spin" />
      </div>
    );
  }

  if (!movie && error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bebas text-white mb-4">{error}</h1>
          <Button onClick={() => navigate('/')} className="bg-netflix-red hover:bg-netflix-red-hover">Retour</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      {/* Barre du haut compacte */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/95 border-b border-white/10 flex-shrink-0">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1 text-white hover:text-netflix-red transition-colors font-work text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>
        <h1 className="font-bebas text-lg text-white truncate max-w-[60%] text-center">{movie?.title}</h1>
        {playerUrl && (
          <button
            onClick={() => window.open(playerUrl, '_blank')}
            className="text-text-secondary hover:text-white transition-colors"
            title="Ouvrir dans Safari"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Lecteur */}
      <div className="flex-1 flex flex-col">
        {playerUrl ? (
          <iframe
            src={playerUrl}
            className="w-full flex-1"
            allowFullScreen
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            frameBorder="0"
            style={{ border: 'none', minHeight: '60vh' }}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
            {loadingPlayer ? (
              <>
                <Loader2 className="w-16 h-16 text-netflix-red animate-spin" />
                <p className="text-white font-work text-lg text-center">Chargement du lecteur...</p>
                <p className="text-text-secondary font-inter text-sm text-center">Merci de patienter quelques secondes</p>
              </>
            ) : error ? (
              <>
                <AlertTriangle className="w-12 h-12 text-yellow-400" />
                <p className="text-white font-work text-lg text-center">{error}</p>
                <div className="flex gap-3">
                  <Button onClick={extractPlayer} className="bg-netflix-red hover:bg-netflix-red-hover">
                    <RefreshCw className="w-4 h-4 mr-2" /> Réessayer
                  </Button>
                  {movie?.tokrav_url && (
                    <Button onClick={() => window.open(movie.tokrav_url, '_blank')} variant="outline" className="border-white/20 text-white">
                      <ExternalLink className="w-4 h-4 mr-2" /> Ouvrir sur Tokrav
                    </Button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Info film sous le lecteur */}
        {movie && playerUrl && (
          <div className="px-4 py-3 bg-bg-primary border-t border-white/10 flex-shrink-0">
            <h2 className="text-lg font-bebas text-white">{movie.title}</h2>
            <div className="flex items-center gap-3 text-text-secondary font-work text-xs">
              {movie.vote_average > 0 && <span className="text-yellow-400">&#11088; {movie.vote_average.toFixed(1)}</span>}
              {movie.release_date && <span>{new Date(movie.release_date).getFullYear()}</span>}
              {movie.runtime && <span>{Math.floor(movie.runtime / 60)}h{movie.runtime % 60 > 0 ? `${movie.runtime % 60}min` : ''}</span>}
              {movie.genres?.length > 0 && <span>{movie.genres.slice(0, 2).join(' · ')}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
